import React, { useState, lazy, Suspense } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Checkbox,
  Form,
  Input,
  Typography,
  Select,
  useToast,
  Loading,
} from '@crisellevaldez/speculo-ui';
import { AlertCircle } from 'lucide-react';

// Lazy load DNS Records Section
const DNSRecordsSection = lazy(() =>
  import('../components/DNSRecordsSection').then((module) => ({
    default: module.DNSRecordsSection,
  }))
);

// Lazy load GoDaddy Delegate Access component
const GoDaddyDelegateAccess = lazy(() =>
  import('../components/GoDaddyDelegateAccess').then((module) => ({
    default: module.GoDaddyDelegateAccess,
  }))
);
import {
  cleanDomainInput,
  DOMAIN_PROVIDERS,
  idxSiteSetupSchema,
  IDXSiteSetupData,
} from '../schemas/onboarding.schema';
import { TwoFaContactFields } from '../components/TwoFaContactFields';
import { useUser } from '../../../../hooks/useUser';
import { validateDomain } from '../../../../schemas/domain.schema';
import { HttpsUrlInput } from '../../../common/HttpsUrlInput';
import { FormSection } from '../components/FormSection';
import { TipBox } from '../components/TipBox';
import { QuestionLabel } from '../components/QuestionLabel';
import { SectionHeading } from '../components/SectionHeading';
import { ChoiceCard } from '../components/ChoiceCard';
import { NextButton } from '../components/NextButton';
import { StageContainer } from '../components/StageContainer';
import { SectionBox } from '../components/SectionBox';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { useOnboardingWebhook } from '../hooks/useOnboardingWebhook';

interface IDXSiteSetupStepProps {
  onNext: (data: IDXSiteSetupData) => void;
  onBack?: () => void;
  initialData?: Partial<IDXSiteSetupData>;
  isLoading?: boolean;
  websiteData?: any; // Data from Stage 2
  orgName?: string;
  brokerageName?: string;
}

const SENDGRID_ERROR = 'Please confirm this domain is not already connected to SendGrid';
const DNS_RECORDS_ERROR = 'Please confirm that you have added the DNS records before launching';
const EMAIL_DOMAIN_ERROR = 'Please enter a valid email domain (e.g., yourcompany.com)';

export const IDXSiteSetupStep: React.FC<IDXSiteSetupStepProps> = ({
  onNext,
  onBack,
  initialData,
  isLoading = false,
  websiteData,
  orgName = '',
  brokerageName = '',
}) => {
  const { organization } = useOrganizationStore();
  const { user } = useUser();
  const { showToast } = useToast();
  const { sendWebhook } = useOnboardingWebhook();

  // Determine website type from Stage 2 data
  const hasWebsite = websiteData?.is_primary_website === 'yes' ? 'primary' : 'no';

  const websiteDomain = websiteData?.domain_name || '';

  // Check if Speculo purchased the domain - no DNS help needed
  const speculoPurchasedDomain = websiteData?.domain_ownership === 'purchase';

  // Get existing logo URL from various sources
  const existingLogoUrl =
    initialData?.logo_url ||
    (organization as any)?.onboardingData?.website?.website_builder?.hero?.logo ||
    organization?.logoUrl ||
    '';

  const [selectedLogo, setSelectedLogo] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(existingLogoUrl);
  const [selectedFavicon, setSelectedFavicon] = useState<File | null>(null);
  const [faviconPreviewUrl, setFaviconPreviewUrl] = useState(
    (initialData as any)?.favicon_url || organization?.faviconUrl || ''
  );
  const [logoSize, setLogoSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [logoPlacement, setLogoPlacement] = useState<'header' | 'both'>('header');
  const [dnsOption, setDnsOption] = useState<'add_dns' | 'need_help' | null>(null);
  const [domainAccessShared, setDomainAccessShared] = useState(false);
  const [dnsRecordsAdded, setDnsRecordsAdded] = useState(false);
  // Remi's email domain: the IDX domain ("Use my IDX domain", which must be free
  // in SendGrid) or another domain the client types.
  const [useIdxDomainForEmail, setUseIdxDomainForEmail] = useState(
    initialData?.email_domain_source === 'same_as_idx'
  );
  const [typedEmailDomain, setTypedEmailDomain] = useState(
    initialData?.email_domain_source === 'different' ? (initialData?.email_domain ?? '') : ''
  );
  const [sendgridConfirmed, setSendgridConfirmed] = useState(
    initialData?.sendgrid_confirmed === true
  );
  // The typed domain the email DNS records were generated for.
  const [emailRecordsDomain, setEmailRecordsDomain] = useState('');

  // Track which button action is in progress: 'skip' | 'launch' | null
  const [actionInProgress, setActionInProgress] = useState<'skip' | 'launch' | null>(null);

  // Compliance links state
  const [complianceLinks, setComplianceLinks] = useState<{ key: string; value: string }[]>([]);
  const [newLinkTitle, setNewLinkTitle] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');

  const logoSizes = {
    small: { width: 100, height: 50 },
    medium: { width: 200, height: 100 },
    large: { width: 260, height: 130 },
  };

  const form = useForm<IDXSiteSetupData>({
    resolver: zodResolver(idxSiteSetupSchema),
    defaultValues: {
      has_website: initialData?.has_website || hasWebsite,
      idx_subdomain: initialData?.idx_subdomain || '',
      new_domain: initialData?.new_domain || '',
      website_domain: websiteDomain,
      logo_url: existingLogoUrl, // Use existing logo URL for validation
      primary_color: initialData?.primary_color || '#000000',
      secondary_color: initialData?.secondary_color || '#000000',
      privacy_policy_url: initialData?.privacy_policy_url || '',
      terms_of_service_url: initialData?.terms_of_service_url || '',
      disclaimer_url: initialData?.disclaimer_url || '',
      // When Speculo purchased the domain, force needs_domain_help to false (no DNS help needed)
      needs_domain_help: speculoPurchasedDomain ? false : initialData?.needs_domain_help || false,
      domain_access_shared: speculoPurchasedDomain
        ? true
        : initialData?.domain_access_shared || false,
      // No auto-fill for registrar fields - always start empty
      domain_registrar: '',
      registrar_username: '',
      registrar_password: '',
      registrar_email: '',
      registrar_phone: '',
      provider_other_name: '',
      login_url: '',
      two_fa_contact_name: '',
      two_fa_contact_email: '',
      two_fa_contact_phone: '',
    },
    mode: 'onBlur',
  });

  const watchHasWebsite = form.watch('has_website');
  const watchIdxSubdomain = form.watch('idx_subdomain');
  const watchNewDomain = form.watch('new_domain');
  const accessMethod = form.watch('access_method');
  const provider = form.watch('provider');
  const setFormValue = <K extends Parameters<typeof form.setValue>[0]>(
    name: K,
    value: Parameters<typeof form.setValue<K>>[1]
  ) => form.setValue(name, value, { shouldValidate: form.formState.isSubmitted });
  // The subdomain needs a valid domain to sit under.
  const newDomain = cleanDomainInput(watchNewDomain ?? '');
  const newDomainReady = validateDomain(newDomain).isValid;
  // The domain Remi's email uses: the root domain the IDX site sits under.
  const idxDomain = (watchHasWebsite === 'no' ? newDomain : websiteDomain) || '';
  const idxDomainReady = validateDomain(idxDomain).isValid;
  const emailDomain = useIdxDomainForEmail ? idxDomain : cleanDomainInput(typedEmailDomain);
  const emailDomainValid = validateDomain(emailDomain).isValid;
  // Email records show once the email domain is settled: the IDX domain confirmed
  // free in SendGrid, or the typed domain's records generated.
  // The email domain step is answered: the IDX domain confirmed free in SendGrid,
  // or a valid typed domain.
  const emailDomainSettled = useIdxDomainForEmail
    ? sendgridConfirmed && idxDomainReady
    : emailDomainValid;
  const emailRecordsReady = useIdxDomainForEmail
    ? sendgridConfirmed && !!idxDomain
    : emailDomainValid && emailRecordsDomain === emailDomain;

  // A new email domain means new records to add and confirm.
  const resetEmailRecords = () => {
    setSendgridConfirmed(false);
    setEmailRecordsDomain('');
    setDnsRecordsAdded(false);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        setLogoPreviewUrl(dataUrl);
        // Sync to form for validation - use data URL as placeholder to satisfy validation
        form.setValue('logo_url', dataUrl, { shouldValidate: true });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFaviconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFavicon(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFaviconPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    showToast({
      message: `${label} copied to clipboard`,
      variant: 'success',
    });
  };

  const handleAddLink = () => {
    if (newLinkTitle && newLinkUrl) {
      setComplianceLinks([
        ...complianceLinks,
        {
          key: newLinkTitle,
          value: newLinkUrl.startsWith('https://') ? newLinkUrl : `https://${newLinkUrl}`,
        },
      ]);
      setNewLinkTitle('');
      setNewLinkUrl('');
    }
  };

  const handleDeleteLink = (linkKey: string) => {
    setComplianceLinks((prevLinks) => prevLinks.filter((link) => link.key !== linkKey));
  };

  const buildSubmissionData = (data: IDXSiteSetupData, skipped: boolean) => {
    return {
      ...data,
      logo_file: selectedLogo,
      favicon_file: selectedFavicon,
      logo_width: logoSizes[logoSize].width,
      logo_height: logoSizes[logoSize].height,
      logo_placement: logoPlacement,
      compliance_links: complianceLinks,
      idx_skipped: skipped,
      // Speculo's access, in the fields the save and Client Admin read.
      ...(data.needs_domain_help && {
        domain_access_shared:
          data.access_method === 'godaddy_delegate' && data.domain_access_shared === true,
        ...(data.access_method === 'credentials' && {
          domain_registrar:
            data.provider_other_name?.trim() ||
            DOMAIN_PROVIDERS.find((p) => p.value === data.provider)?.label,
          registrar_email: data.two_fa_contact_email,
          registrar_phone: data.two_fa_contact_phone,
        }),
      }),
      email_domain_source: useIdxDomainForEmail ? 'same_as_idx' : 'different',
      email_domain: emailDomain,
      sendgrid_confirmed: useIdxDomainForEmail && sendgridConfirmed,
    };
  };

  // Set on a blocked submit; each confirmation's error shows until it's ticked.
  const [showConfirmErrors, setShowConfirmErrors] = useState(false);
  const sendgridError =
    showConfirmErrors && useIdxDomainForEmail && !sendgridConfirmed ? SENDGRID_ERROR : undefined;
  const emailDomainError =
    showConfirmErrors && !useIdxDomainForEmail && !emailDomainValid
      ? EMAIL_DOMAIN_ERROR
      : undefined;
  const dnsRecordsError = showConfirmErrors && !dnsRecordsAdded ? DNS_RECORDS_ERROR : undefined;

  /** Blocks submit until the required confirmations are ticked. */
  const confirmationsMissing = (requireDnsRecords: boolean) => {
    // Speculo purchased the domain, so it handles DNS and SendGrid.
    if (speculoPurchasedDomain) return false;
    const missing =
      useIdxDomainForEmail && !sendgridConfirmed
        ? SENDGRID_ERROR
        : !useIdxDomainForEmail && !emailDomainValid
          ? EMAIL_DOMAIN_ERROR
          : requireDnsRecords && !dnsRecordsAdded
            ? DNS_RECORDS_ERROR
            : null;
    if (!missing) return false;
    setShowConfirmErrors(true);
    showToast({ message: missing });
    return true;
  };

  const handleSubmit = (data: IDXSiteSetupData) => {
    if (confirmationsMissing(true)) return;

    // Set loading state for launch button
    setActionInProgress('launch');
    // Pass file objects, size, placement, and compliance links to parent
    const submissionData = buildSubmissionData(data, false);
    onNext(submissionData as any);
  };

  const handleSkipSubmit = (data: IDXSiteSetupData) => {
    if (confirmationsMissing(false)) return;
    // Set loading state for skip button
    setActionInProgress('skip');
    // Save current form data but mark as skipped for different webhook handling
    const submissionData = buildSubmissionData(data, true);
    onNext(submissionData as any);
  };

  // Calculate full subdomain URL
  const getFullSubdomainUrl = () => {
    const subdomain = watchIdxSubdomain;
    if (!subdomain) return '';

    if (watchHasWebsite === 'no' && newDomain) {
      return `${subdomain}.${newDomain}`;
    } else if (websiteDomain) {
      return `${subdomain}.${websiteDomain}`;
    }
    return subdomain;
  };

  return (
    <StageContainer
      title="IDX Site Setup"
      description="Set up the IDX site Remi uses to send properties to your leads by email and SMS: its domain, branding, and DNS."
    >
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
        {/* Domain Configuration */}
        <div className="space-y-6">
          <SectionHeading title="Domain Configuration" />

          {watchHasWebsite === 'no' ? (
            <>
              <FormSection>
                <Form.Control required>
                  <QuestionLabel>New Domain for IDX Site</QuestionLabel>
                  <Controller
                    name="new_domain"
                    control={form.control}
                    render={({ field }) => (
                      <HttpsUrlInput
                        {...field}
                        value={field.value ?? ''}
                        // Saved as the bare domain: path, trailing slash and casing are dropped.
                        onChange={(e) => {
                          // A new domain means new DNS records to confirm.
                          if (useIdxDomainForEmail) resetEmailRecords();
                          setDnsRecordsAdded(false);
                          field.onChange(e);
                        }}
                        onBlur={() => {
                          form.setValue('new_domain', cleanDomainInput(field.value ?? ''), {
                            shouldValidate: true,
                          });
                          field.onBlur();
                        }}
                        placeholder="example.com"
                        error={form.formState.errors.new_domain?.message}
                      />
                    )}
                  />
                  <TipBox>
                    Enter the domain you want to use for your IDX site (e.g., yourcompany.com)
                  </TipBox>
                </Form.Control>
              </FormSection>

              <FormSection>
                <Form.Control required>
                  <QuestionLabel>IDX Subdomain</QuestionLabel>
                  <Controller
                    name="idx_subdomain"
                    control={form.control}
                    render={({ field }) => (
                      <div className="flex items-center gap-2">
                        <HttpsUrlInput
                          {...field}
                          value={field.value ?? ''}
                          disabled={!newDomainReady}
                          error={form.formState.errors.idx_subdomain?.message}
                          className="flex-1"
                        />
                        <span className="text-gray-500">.{newDomain || 'domain.com'}</span>
                      </div>
                    )}
                  />
                  <TipBox>
                    When Remi sends properties to your leads by email or SMS, it links them directly
                    to your IDX site.{' '}
                    {newDomainReady ? (
                      <>
                        Your IDX site will be accessible at:{' '}
                        <strong>https://{getFullSubdomainUrl()}</strong>
                      </>
                    ) : (
                      'Enter your domain above first.'
                    )}
                  </TipBox>
                </Form.Control>
              </FormSection>
            </>
          ) : (
            <FormSection>
              <Form.Control required>
                <QuestionLabel>IDX Subdomain</QuestionLabel>
                <Controller
                  name="idx_subdomain"
                  control={form.control}
                  render={({ field }) => (
                    <div className="flex items-center gap-2">
                      <HttpsUrlInput
                        {...field}
                        value={field.value ?? ''}
                        error={form.formState.errors.idx_subdomain?.message}
                        className="flex-1"
                      />
                      <span className="text-gray-500">.{websiteDomain || 'domain.com'}</span>
                    </div>
                  )}
                />
                <TipBox>
                  When Remi sends properties to your leads by email or SMS, it links them directly
                  to your IDX site. Your IDX site will be accessible at:{' '}
                  <strong>{getFullSubdomainUrl()}</strong>
                </TipBox>
              </Form.Control>
            </FormSection>
          )}
        </div>

        {/* Branding */}
        <div className="space-y-6">
          <SectionHeading title="Branding" />

          <FormSection>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <Form.Control required>
                <QuestionLabel>Logo</QuestionLabel>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('logo-upload')?.click()}
                    >
                      Browse
                    </Button>
                    <Select
                      value={logoSize}
                      onChange={(value) => {
                        if (typeof value === 'string') {
                          setLogoSize(value as 'small' | 'medium' | 'large');
                        }
                      }}
                      options={[
                        { value: 'small', label: 'Small' },
                        { value: 'medium', label: 'Medium' },
                        { value: 'large', label: 'Large' },
                      ]}
                    />
                    <Select
                      value={logoPlacement}
                      onChange={(value) => {
                        if (typeof value === 'string') {
                          setLogoPlacement(value as 'header' | 'both');
                        }
                      }}
                      options={[
                        { value: 'header', label: 'Header Only' },
                        { value: 'both', label: 'Header & Footer' },
                      ]}
                    />
                  </div>
                  {(selectedLogo || logoPreviewUrl) && (
                    <div className="flex justify-start">
                      <div
                        className="rounded-md overflow-hidden bg-gray-50"
                        style={{
                          width: logoSizes[logoSize].width,
                          height: logoSizes[logoSize].height,
                        }}
                      >
                        <img
                          src={selectedLogo ? URL.createObjectURL(selectedLogo) : logoPreviewUrl}
                          alt="Logo preview"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                  )}
                </div>
                {form.formState.errors.logo_url?.message && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                    {form.formState.errors.logo_url.message}
                  </p>
                )}
                <TipBox>Logo must meet MLS and Brokerage Compliance standards.</TipBox>
              </Form.Control>
              {/* Hidden file input moved outside Form.Control to avoid required attribute issue */}
              <input
                id="logo-upload"
                type="file"
                className="hidden"
                accept="image/*"
                onChange={handleLogoChange}
              />

              <div>
                <Form.Control>
                  <QuestionLabel required={false}>Favicon</QuestionLabel>
                  <div className="flex items-center gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('favicon-upload')?.click()}
                    >
                      Browse
                    </Button>
                    {(selectedFavicon || faviconPreviewUrl) && (
                      <div className="w-8 h-8 rounded-md overflow-hidden bg-gray-50">
                        <img
                          src={
                            selectedFavicon
                              ? URL.createObjectURL(selectedFavicon)
                              : faviconPreviewUrl
                          }
                          alt="Favicon preview"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}
                  </div>
                </Form.Control>
                {/* Hidden file input moved outside Form.Control for consistency */}
                <input
                  id="favicon-upload"
                  type="file"
                  className="hidden"
                  accept="image/x-icon,image/png"
                  onChange={handleFaviconChange}
                />
              </div>
            </div>
          </FormSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormSection>
              <Form.Control>
                <QuestionLabel>Primary Color</QuestionLabel>
                <Controller
                  name="primary_color"
                  control={form.control}
                  render={({ field }) => (
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        className="h-10 w-10 rounded-md cursor-pointer border"
                      />
                      <Input {...field} className="uppercase flex-1" />
                    </div>
                  )}
                />
              </Form.Control>
            </FormSection>

            <FormSection>
              <Form.Control>
                <QuestionLabel>Secondary Color</QuestionLabel>
                <Controller
                  name="secondary_color"
                  control={form.control}
                  render={({ field }) => (
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        className="h-10 w-10 rounded-md cursor-pointer border"
                      />
                      <Input {...field} className="uppercase flex-1" />
                    </div>
                  )}
                />
              </Form.Control>
            </FormSection>
          </div>
        </div>

        {/* Compliance Links */}
        <div className="space-y-6">
          <SectionHeading title="Compliance Links" />

          <FormSection>
            <TipBox>Compliance links appear at the footer of your page.</TipBox>

            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-[1fr,2fr,auto] gap-4">
                <Input
                  placeholder="Enter title"
                  value={newLinkTitle}
                  onChange={(e) => setNewLinkTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newLinkTitle && newLinkUrl) {
                        handleAddLink();
                      }
                    }
                  }}
                />
                <Input
                  placeholder="https://example.com"
                  value={newLinkUrl}
                  onChange={(e) => setNewLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newLinkTitle && newLinkUrl) {
                        handleAddLink();
                      }
                    }
                  }}
                />
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setNewLinkTitle('');
                      setNewLinkUrl('');
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleAddLink}
                    disabled={!newLinkTitle || !newLinkUrl}
                  >
                    Add Link
                  </Button>
                </div>
              </div>

              {complianceLinks.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-2 text-sm font-semibold">Title</th>
                        <th className="text-left px-4 py-2 text-sm font-semibold">URL</th>
                        <th className="text-center px-4 py-2 text-sm font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {complianceLinks.map((link, index) => (
                        <tr key={index} className="border-b last:border-b-0">
                          <td className="px-4 py-2 text-sm">{link.key}</td>
                          <td className="px-4 py-2 text-sm truncate max-w-xs">{link.value}</td>
                          <td className="px-4 py-2 text-center">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteLink(link.key)}
                              className="text-white hover:bg-red-600 bg-red-700 border-0"
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center text-gray-500 py-4 border rounded-lg">
                  No compliance links added yet
                </div>
              )}
            </div>
          </FormSection>
        </div>

        {/* DNS Help Options - Only show if user owns the domain (not purchased by Speculo) */}
        {!speculoPurchasedDomain && (
          <div className="space-y-6">
            <SectionHeading title="IDX and Email DNS Setup" />

            {/* Step 1: who adds the DNS records */}
            <FormSection>
              <QuestionLabel>
                How would you like to add your IDX and email DNS records?
              </QuestionLabel>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <ChoiceCard
                  name="dns_option"
                  value="add_dns"
                  checked={dnsOption === 'add_dns'}
                  onSelect={() => {
                    setDnsOption('add_dns');
                    form.setValue('needs_domain_help', false);
                  }}
                  title="I'll add the DNS records myself"
                  description="We'll give you the DNS records to add at your domain provider."
                />
                <ChoiceCard
                  name="dns_option"
                  value="need_help"
                  checked={dnsOption === 'need_help'}
                  onSelect={() => {
                    setDnsOption('need_help');
                    form.setValue('needs_domain_help', true);
                  }}
                  title="I'd like Speculo to set it up"
                  description="Give Speculo access to your domain provider and we'll add the records for you."
                />
              </div>
            </FormSection>

            {/* Step 2: the email domain, same as Email Setup on "Use my own IDX" */}
            {dnsOption && (
              <FormSection>
                <Form.Control required>
                  <QuestionLabel>Which domain should Remi send email from?</QuestionLabel>
                  <div className="mt-3 space-y-3">
                    <Checkbox
                      id="remi-idx-email-use-idx-domain"
                      checked={useIdxDomainForEmail}
                      onChange={(e) => {
                        setUseIdxDomainForEmail(e.target.checked);
                        resetEmailRecords();
                      }}
                      label="Use my IDX domain"
                    />
                    {useIdxDomainForEmail ? (
                      <HttpsUrlInput
                        value={idxDomain}
                        readOnly
                        placeholder="Enter your IDX site domain above"
                      />
                    ) : (
                      <HttpsUrlInput
                        value={typedEmailDomain}
                        onChange={(e) => {
                          setTypedEmailDomain(e.target.value);
                          resetEmailRecords();
                        }}
                        // Kept as the bare domain: path, trailing slash and casing are dropped.
                        onBlur={() => setTypedEmailDomain(cleanDomainInput(typedEmailDomain))}
                        placeholder="yourcompany.com"
                        error={emailDomainError}
                      />
                    )}
                  </div>
                  <TipBox>
                    Remi sends emails to your leads from this domain. Untick &quot;Use my IDX
                    domain&quot; and enter another domain if your IDX domain is already connected to
                    SendGrid.
                  </TipBox>
                </Form.Control>
                {useIdxDomainForEmail && (
                  <SectionBox variant="gray" type="child" className="mt-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="idx-domain-sendgrid-confirmed"
                          checked={sendgridConfirmed}
                          onChange={(e) => {
                            setSendgridConfirmed(e.target.checked);
                            // Unticking hides the records, so they must be confirmed again.
                            if (!e.target.checked) setDnsRecordsAdded(false);
                          }}
                          label="I confirm this domain is not already connected to SendGrid."
                          disabled={!idxDomainReady}
                        />
                        <span className="text-red-600 dark:text-red-400 text-sm">*</span>
                      </div>
                      {!idxDomainReady && (
                        <p className="mt-1 text-sm text-gray-500">
                          Enter your IDX domain above first.
                        </p>
                      )}
                      {sendgridError && (
                        <p className="mt-1 text-sm text-destructive">{sendgridError}</p>
                      )}
                    </div>
                  </SectionBox>
                )}
              </FormSection>
            )}

            {/* Step 3a: the IDX + email records the client adds */}
            {dnsOption === 'add_dns' && emailDomainSettled && (
              <FormSection>
                {emailRecordsReady ? (
                  <Suspense fallback={<Loading />}>
                    <DNSRecordsSection
                      subdomain={watchIdxSubdomain || ''}
                      domain={idxDomain}
                      emailDomain={emailDomain}
                      checked={dnsRecordsAdded}
                      onCheckedChange={(checked) => {
                        if (!watchIdxSubdomain?.trim()) {
                          showToast({
                            message: 'Please enter the IDX subdomain first',
                          });
                          return;
                        }
                        setDnsRecordsAdded(checked);
                        // Send webhook when checkbox is checked
                        if (checked) {
                          sendWebhook('Stage 4 - DNS Records Added Confirmation', {
                            idx_subdomain: watchIdxSubdomain,
                            full_domain: getFullSubdomainUrl(),
                            dns_records_confirmed: true,
                            timestamp: new Date().toISOString(),
                          });
                          showToast({
                            message: 'Thank you! DNS records confirmation received.',
                            variant: 'success',
                          });
                        }
                      }}
                      showCheckbox={true}
                      checkboxLabel="I have added the DNS records"
                      error={dnsRecordsError}
                      checkboxDisabled={!watchIdxSubdomain?.trim()}
                      helperMessage={
                        !watchIdxSubdomain?.trim()
                          ? 'Please fill in the IDX subdomain above before confirming'
                          : undefined
                      }
                    />
                  </Suspense>
                ) : (
                  <div>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => setEmailRecordsDomain(emailDomain)}
                    >
                      Generate DNS Records
                    </Button>
                  </div>
                )}
              </FormSection>
            )}

            {/* Step 3b: access for Speculo to add the records (same as Email Setup) */}
            {dnsOption === 'need_help' && emailDomainSettled && (
              <FormSection>
                <QuestionLabel>How would you like to give Speculo access?</QuestionLabel>
                <TipBox>Speculo will add your IDX and email DNS records with this access.</TipBox>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <ChoiceCard
                    name="remi-idx-access-method"
                    value="godaddy_delegate"
                    checked={accessMethod === 'godaddy_delegate'}
                    onSelect={() => setFormValue('access_method', 'godaddy_delegate')}
                    title="Share domain-only access (GoDaddy)"
                    description="Invite Speculo as a delegate. No password needed."
                  />
                  <ChoiceCard
                    name="remi-idx-access-method"
                    value="credentials"
                    checked={accessMethod === 'credentials'}
                    onSelect={() => setFormValue('access_method', 'credentials')}
                    title="Share my login credentials"
                    description="Share the login for your domain provider."
                  />
                </div>
                {form.formState.errors.access_method?.message && (
                  <p className="mt-2 text-sm text-destructive">
                    {form.formState.errors.access_method.message}
                  </p>
                )}

                {accessMethod === 'godaddy_delegate' && (
                  <SectionBox variant="gray" type="child" className="mt-4">
                    <Suspense fallback={<Loading />}>
                      <GoDaddyDelegateAccess
                        checked={domainAccessShared}
                        onCheckedChange={(checked) => {
                          if (!watchIdxSubdomain?.trim()) {
                            showToast({
                              message: 'Please enter the IDX subdomain first',
                            });
                            return;
                          }
                          setDomainAccessShared(checked);
                          // Sync to form for validation
                          form.setValue('domain_access_shared', checked);
                          // Send webhook when checkbox is checked
                          if (checked) {
                            sendWebhook('Stage 4 - IDX Domain Access Shared', {
                              idx_subdomain: watchIdxSubdomain,
                              full_domain: getFullSubdomainUrl(),
                              domain_access_shared: true,
                              timestamp: new Date().toISOString(),
                            });
                            showToast({
                              message: 'Thank you! We will verify the access soon.',
                              variant: 'success',
                            });
                          }
                        }}
                        checkboxLabel="I've sent domain-only access to Speculo."
                        error={form.formState.errors.domain_access_shared?.message}
                      />
                    </Suspense>
                  </SectionBox>
                )}

                {accessMethod === 'credentials' && (
                  <SectionBox variant="gray" type="child" className="mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Form.Control required className="md:col-span-2">
                        <Form.Label>Domain provider</Form.Label>
                        <Controller
                          control={form.control}
                          name="provider"
                          render={({ field }) => (
                            <Select
                              value={field.value ?? ''}
                              onChange={(value) =>
                                field.onChange(Array.isArray(value) ? value[0] : value)
                              }
                              options={DOMAIN_PROVIDERS.map((p) => ({
                                value: p.value,
                                label: p.label,
                              }))}
                              placeholder="Select your domain provider"
                              error={form.formState.errors.provider?.message}
                            />
                          )}
                        />
                      </Form.Control>

                      {provider === 'other' && (
                        <Form.Control required className="md:col-span-2">
                          <Form.Label>Domain provider name</Form.Label>
                          <Input
                            {...form.register('provider_other_name')}
                            placeholder="e.g., Google Domains"
                            error={form.formState.errors.provider_other_name?.message}
                          />
                        </Form.Control>
                      )}

                      {provider && provider !== 'godaddy' && (
                        <Form.Control required className="md:col-span-2">
                          <Form.Label>Login URL</Form.Label>
                          <HttpsUrlInput
                            {...form.register('login_url')}
                            placeholder="yourprovider.com/login"
                            error={form.formState.errors.login_url?.message}
                          />
                        </Form.Control>
                      )}

                      <Form.Control required>
                        <Form.Label>Username</Form.Label>
                        <Input
                          {...form.register('registrar_username')}
                          placeholder="Username or email"
                          autoComplete="off"
                          error={form.formState.errors.registrar_username?.message}
                        />
                      </Form.Control>
                      <Form.Control required>
                        <Form.Label>Password</Form.Label>
                        <Input
                          {...form.register('registrar_password')}
                          type="password"
                          placeholder="Enter password"
                          autoComplete="new-password"
                          error={form.formState.errors.registrar_password?.message}
                        />
                      </Form.Control>

                      <TwoFaContactFields
                        form={form}
                        fieldPrefix=""
                        user={user}
                        purpose="your domain provider account"
                      />
                    </div>
                  </SectionBox>
                )}
              </FormSection>
            )}
          </div>
        )}

        {/* Go-Live Status Notice */}
        {dnsOption === 'add_dns' && !speculoPurchasedDomain && (
          <FormSection variant="primary">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-yellow-900 mb-2">Go-Live Status</p>
                <Typography variant="p" className="text-sm text-yellow-900">
                  Your IDX site will begin pushing DNS records upon completion. This propagation
                  process can take up to 24 hours for all internet servers to update. Check back
                  soon! P.S. Property data pending MLS approval ⏳
                </Typography>
              </div>
            </div>
          </FormSection>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center gap-3">
          {onBack ? (
            <NextButton type="button" onClick={onBack} variant="secondary" disabled={isLoading}>
              Back to MLS Setup
            </NextButton>
          ) : (
            <div />
          )}
          <div className="flex gap-3">
            {/* Show Next button only for "need_help" option */}
            {dnsOption === 'need_help' && (
              <NextButton
                type="button"
                onClick={form.handleSubmit(handleSkipSubmit)}
                isLoading={isLoading && actionInProgress === 'skip'}
                disabled={isLoading && actionInProgress !== 'skip'}
              >
                Next
              </NextButton>
            )}
            {/* Show Launch button only for "add_dns" option OR when Speculo purchased domain */}
            {(dnsOption === 'add_dns' || speculoPurchasedDomain) && (
              <NextButton
                type="submit"
                isLoading={isLoading && actionInProgress === 'launch'}
                disabled={isLoading && actionInProgress !== 'launch'}
              >
                Launch IDX NOW
              </NextButton>
            )}
            {/* Hide buttons when no option selected and domain not purchased - only show back button */}
          </div>
        </div>
      </form>
    </StageContainer>
  );
};
